import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { authApi } from '@/services/auth.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button } from '@/components/ui';
import { LocalePicker } from '@/components/locale-picker';
import { LogoMark } from '@/components/logo-mark';

type Status = 'working' | 'error';

/**
 * Where every "Sign in with X" redirect lands, whichever way it ended.
 *
 * The backend never redirects here with a bearer token in the query string
 * — `handoff` is a one-time opaque code good for a single
 * `POST /auth/oauth/handoff`, exactly so a token never sits in browser
 * history or a proxy's access log. `error` means the backend already
 * decided the sign-in cannot complete (no matching account, an unverified
 * email, a provider failure) and is telling us which.
 */
export function OAuthCallbackPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { applyExternalLogin } = useAuth();
  const [searchParams] = useSearchParams();
  const attempted = useRef(false);

  const handoff = searchParams.get('handoff');
  const errorCode = searchParams.get('error');
  const [status, setStatus] = useState<Status>(handoff ? 'working' : 'error');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!handoff || attempted.current) return;
    attempted.current = true;
    const remember = searchParams.get('remember') === '1';

    void (async () => {
      try {
        const result = await authApi.oauthHandoff(handoff, remember);

        if ('mfaRequired' in result) {
          void navigate('/mfa-challenge', {
            replace: true,
            state: {
              mfaToken: result.mfaToken,
              availableFactors: result.availableFactors,
              rememberMe: remember,
            },
          });
          return;
        }

        applyExternalLogin(result);
        void navigate(result.tenantSelected ? '/' : '/select-church', { replace: true });
      } catch (caught) {
        const code = caught instanceof ApiError ? caught.code : 'INTERNAL_ERROR';
        setMessage(t(`errors.${code}`, { fallback: t('errors.INTERNAL_ERROR') }));
        setStatus('error');
      }
    })();
  }, [handoff, searchParams, navigate, applyExternalLogin, t]);

  return (
    <main className="auth-page">
      <div className="auth-card">
        <header className="auth-header">
          <LogoMark size={40} />
          <h1 className="auth-title">TABITA</h1>
        </header>

        {status === 'working' && <p>{t('auth.oauth.connecting')}</p>}

        {status === 'error' && (
          <>
            <Alert tone="danger">
              {message ??
                t(`errors.${errorCode ?? 'OAUTH_PROVIDER_ERROR'}`, {
                  fallback: t('auth.oauth.genericError'),
                })}
            </Alert>
            <Button type="button" variant="primary" onClick={() => void navigate('/login')}>
              {t('auth.backToSignIn')}
            </Button>
            {errorCode === 'OAUTH_NO_ACCOUNT' && (
              <Link to="/register" className="auth-secondary-link">
                {t('auth.registerChurch')}
              </Link>
            )}
          </>
        )}

        <footer className="auth-footer">
          <LocalePicker />
        </footer>
      </div>
    </main>
  );
}
